import { pretestQuestions as legacyQuestions } from '@/data/courseData'
import type { AbilityDimension, Question, QuestionType } from '@/entities/question/types'
import type { SupportedGrade } from '@/entities/student/types'

const supportedGrades = new Set<SupportedGrade>(['G4'])

const promptAudioUrls: Partial<Record<string, string>> = {
  'pt-g4-2': '/media/pretest/prompts-v1/pt-g4-2.mp3',
  'pt-g4-3': '/media/pretest/prompts-v1/pt-g4-3.mp3',
  'pt-g4-4': '/media/pretest/prompts-v1/pt-g4-4.mp3',
  'pt-g4-6': '/media/pretest/prompts-v1/pt-g4-6.mp3',
  'pt-g4-9': '/media/pretest/prompts-v1/pt-g4-9.mp3',
  'pt-g4-10': '/media/pretest/prompts-v1/pt-g4-10.mp3',
}

const knowledgeMetadata: Record<string, { unitId: string; knowledgePointIds: string[] }> = {
  'pt-g4-1': { unitId: 'g4-phonics-foundation', knowledgePointIds: ['phonics_vowel_contrast'] },
  'pt-g4-2': { unitId: 'g4-phonics-foundation', knowledgePointIds: ['phonics_short_e'] },
  'pt-g4-3': { unitId: 'g4-phonics-foundation', knowledgePointIds: ['phonics_short_i'] },
  'pt-g4-4': { unitId: 'g4-phonics-foundation', knowledgePointIds: ['phonics_short_u'] },
  'pt-g4-5': { unitId: 'g4-u1-classroom', knowledgePointIds: ['classroom_objects', 'word_categories'] },
  'pt-g4-6': { unitId: 'g4-u1-classroom', knowledgePointIds: ['classroom_objects', 'location_prepositions'] },
  'pt-g4-7': { unitId: 'g4-u1-classroom', knowledgePointIds: ['classroom_objects_spelling'] },
  'pt-g4-8': { unitId: 'g4-u1-classroom', knowledgePointIds: ['classroom_objects_spelling'] },
  'pt-g4-9': { unitId: 'g4-u4-family', knowledgePointIds: ['who_is_pattern', 'family_members'] },
  'pt-g4-10': { unitId: 'g4-s1-daily-life', knowledgePointIds: ['how_do_you_go_pattern', 'transportation'] },
}

function getQuestionType(label: string, hasOptions: boolean): QuestionType {
  if (!hasOptions || label.includes('跟读')) return 'speaking'
  if (label.includes('听')) return 'listening'
  if (label.includes('补全')) return 'fill-blank'
  return 'single-choice'
}

function getDimensions(dimensionLabel: string, type: QuestionType): Partial<Record<AbilityDimension, number>> {
  if (type === 'speaking') return { speaking: 0.6, phonics: 0.4 }
  if (dimensionLabel.includes('拼读') || dimensionLabel.includes('语音')) {
    return type === 'listening' ? { phonics: 0.65, listening: 0.35 } : { phonics: 1 }
  }
  if (dimensionLabel.includes('词汇')) {
    if (type === 'fill-blank') return { vocabulary: 0.65, writing: 0.35 }
    return type === 'listening' ? { vocabulary: 0.65, listening: 0.35 } : { vocabulary: 0.8, reading: 0.2 }
  }
  if (dimensionLabel.includes('句法') || dimensionLabel.includes('指令')) {
    return type === 'listening' ? { grammar: 0.65, listening: 0.35 } : { grammar: 0.8, reading: 0.2 }
  }
  return { reading: 1 }
}

function getDifficulty(index: number, label: string): 1 | 2 | 3 | 4 | 5 {
  if (label.includes('补全')) return 3
  if (index < 2) return 1
  if (index < 6) return 2
  return 3
}

function getAudioText(content: string, type: QuestionType): string | undefined {
  if (type !== 'speaking' && type !== 'listening') return undefined

  const dialogue = content
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => /^[AB]:\s*/.test(line))
    .map((line) => line.replace(/^[AB]:\s*/, ''))
  if (dialogue.length) return dialogue.join(' ')

  return content.split('：').pop()?.trim()
}

export const pretestQuestionData: Question[] = legacyQuestions
  .filter((question) => supportedGrades.has(question.grade as SupportedGrade))
  .map((question, index) => {
    const type = getQuestionType(question.questionType, question.options.length > 0)
    const options = question.options.map((text, optionIndex) => ({ id: String(optionIndex), text }))
    const audioText = getAudioText(question.content, type)
    const knowledge = knowledgeMetadata[question.id] ?? { unitId: 'g4-s1-general', knowledgePointIds: [] }

    return {
      id: question.id,
      grade: question.grade as SupportedGrade,
      unitId: knowledge.unitId,
      knowledgePointIds: knowledge.knowledgePointIds,
      type,
      prompt: question.content,
      options,
      correctAnswer: type === 'speaking' ? undefined : String(question.correctAnswer),
      difficulty: getDifficulty(index, question.questionType),
      dimensions: getDimensions(question.dimension, type),
      dimensionLabel: question.dimension,
      questionTypeLabel: type === 'speaking' ? '口语跟读' : question.questionType,
      explanation: type === 'speaking' ? '只有真实声学评分可以形成口语分数；服务或麦克风不可用时记为未测，不按完成度计分。' : undefined,
      media: audioText ? { audioText, audioUrl: promptAudioUrls[question.id] } : undefined,
    }
  })
